import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight } from "lucide-react";
import { generateNHProposalPDF } from "@/lib/nhProposalGenerator";
import BridgeModelLogo from "@/components/site/BridgeModelLogo";
import DeploymentSwitcher from "@/components/site/DeploymentSwitcher";
import { deployments } from "@/lib/deployments";

const nhDeployment = deployments.find((d) => d.id === "north-houston");

export default function NavigationNH() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const links = [
    { href: "#story", label: "Story" },
    { href: "#alignment", label: "Alignment" },
    { href: "#context", label: "Context" },
    { href: "#trades", label: "Corridor" },
    { href: "#seniors", label: "Seniors" },
    { href: "#founder", label: "Founder" },
    { href: "#budget", label: "Budget" },
    { href: "#federal", label: "Federal" },
    { href: "#partnership", label: "District Ask" },
    { href: "#contact", label: "Contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = links.map((l) => l.href.replace("#", ""));
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (sections.length === 0) return;
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.set(entry.target.id, entry.intersectionRatio);
          else visible.delete(entry.target.id);
        });
        if (visible.size > 0) {
          const top = [...visible.entries()].sort((a, b) => b[1] - a[1])[0][0];
          setActiveSection(top);
        }
      },
      { rootMargin: "-80px 0px -55% 0px", threshold: [0.1, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="site-nav-nh"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#F9F7F1]/95 backdrop-blur border-b border-[#CBD5E1]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <a href="/" data-testid="nav-nh-logo" className="flex items-center gap-3 group shrink-0">
          <BridgeModelLogo
            size={40}
            primary={scrolled ? "#0A192F" : "#F9F7F1"}
            accent={scrolled ? "#8B0000" : "#D4AF37"}
            sunGlow={scrolled ? "#D4AF37" : "#F4D77A"}
          />
          <div className="leading-tight">
            <div className={`font-display text-lg leading-none ${scrolled ? "text-[#0A192F]" : "text-[#F9F7F1]"}`}>
              The <span className="italic">Bridge</span> Model
            </div>
            <div className={`font-mono text-[9px] uppercase tracking-[0.2em] mt-1 ${scrolled ? "text-[#8B0000]" : "text-[#D4AF37]"}`}>
              Proposal 0{nhDeployment.deploymentNumber} · {nhDeployment.city}
            </div>
          </div>
        </a>

        <nav className="hidden 2xl:flex items-center gap-5">
          {links.map((l) => {
            const id = l.href.replace("#", "");
            const isActive = activeSection === id;
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-nh-link-${id}`}
                data-active={isActive ? "true" : "false"}
                className={`relative text-[11px] font-mono uppercase tracking-widest whitespace-nowrap transition-colors pb-1 ${
                  isActive
                    ? scrolled
                      ? "text-[#8B0000]"
                      : "text-[#D4AF37]"
                    : scrolled
                    ? "text-[#334155] hover:text-[#8B0000]"
                    : "text-[#E2E8F0] hover:text-[#D4AF37]"
                }`}
              >
                {l.label}
                {isActive && (
                  <span
                    aria-hidden
                    className={`absolute -bottom-0.5 left-0 right-0 h-px ${scrolled ? "bg-[#8B0000]" : "bg-[#D4AF37]"}`}
                  />
                )}
              </a>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <DeploymentSwitcher currentId="north-houston" scrolled={scrolled} />
          <Button
            data-testid="nav-nh-download-pdf"
            variant="ghost"
            onClick={generateNHProposalPDF}
            className={`rounded-none font-mono text-[11px] uppercase tracking-widest h-9 px-3 ${
              scrolled
                ? "text-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F7F1]"
                : "text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F]"
            }`}
          >
            Download PDF
          </Button>
          <Button
            data-testid="nav-nh-schedule-cta"
            asChild
            className="rounded-none bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] font-mono text-[11px] uppercase tracking-widest h-9 px-4"
          >
            <a href="#contact">
              Schedule <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </a>
          </Button>
        </div>

        <button
          data-testid="nav-nh-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 ${scrolled ? "text-[#0A192F]" : "text-[#F9F7F1]"}`}
          aria-label="Toggle navigation"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F7F1] border-t border-[#CBD5E1] px-6 py-6 flex flex-col gap-4">
          {links.map((l) => {
            const id = l.href.replace("#", "");
            const isActive = activeSection === id;
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className={`font-mono text-xs uppercase tracking-widest transition-colors ${
                  isActive ? "text-[#8B0000] pl-3 border-l-2 border-[#8B0000]" : "text-[#0A192F]"
                }`}
              >
                {l.label}
              </a>
            );
          })}
          <Link
            to="/baytown"
            onClick={() => setOpen(false)}
            className="font-mono text-xs uppercase tracking-widest text-[#8B0000]"
          >
            Switch to Proposal 01 · Baytown →
          </Link>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="font-mono text-xs uppercase tracking-widest text-[#475569]"
          >
            ← Back to The Bridge Model home
          </Link>
          <div className="flex gap-2 pt-3 border-t border-[#CBD5E1]">
            <Button
              onClick={() => {
                generateNHProposalPDF();
                setOpen(false);
              }}
              variant="outline"
              className="rounded-none flex-1 border-[#0A192F] text-[#0A192F] font-mono text-xs uppercase tracking-widest"
            >
              PDF
            </Button>
            <Button
              asChild
              onClick={() => setOpen(false)}
              className="rounded-none flex-1 bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] font-mono text-xs uppercase tracking-widest"
            >
              <a href="#contact">Schedule</a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
